import React, { useContext } from 'react';
import { Button, Image, Link, Text } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LoginErrorCard } from './login-error-card';
import GoogleIcon from '../media/btn-google.svg';
import MicrosoftIcon from '../media/btn-microsoft.svg';

const LEARN_MORE_URL = 'https://help.ads.microsoft.com/#apex/ads/en/60259/0';

export type ProviderAccountRequiredPageProps = {
  email?: string | null;
  provider: 'work' | 'personal' | 'google' | 'microsoft';
  onContinue: () => void;
  onGoBack: () => void;
  onLearnMore?: () => void;
};

export const ProviderAccountRequiredPage: React.FC<ProviderAccountRequiredPageProps> = ({
  email,
  provider,
  onContinue,
  onGoBack,
  onLearnMore,
}) => {
  const i18n = useContext(I18nContext);
  const isGoogle = provider === 'google';
  let title: string;
  let description: string;

  switch (provider) {
    case 'personal':
      title = i18n.getString(_TL_('Sign in with your Microsoft Personal account'));
      description = i18n.getString(_TL_('When Microsoft asks which account to use, choose your Personal account, not your Work or school account.'));
      break;
    case 'google':
      title = i18n.getString(_TL_('Use Google sign-in for this email'));
      description = email
        ? i18n.getString(_TL_('To access Microsoft Advertising with {{email}}, sign in with Google.'), { email })
        : i18n.getString(_TL_('To access Microsoft Advertising with this email, sign in with Google.'));
      break;
    case 'microsoft':
      title = i18n.getString(_TL_('Use Microsoft sign-in for this email'));
      description = email
        ? i18n.getString(_TL_('To access Microsoft Advertising with {{email}}, sign in with Microsoft.'), { email })
        : i18n.getString(_TL_('To access Microsoft Advertising with this email, sign in with Microsoft.'));
      break;
    case 'work':
    default:
      title = i18n.getString(_TL_('Sign in with your Microsoft work or school account'));
      description = i18n.getString(_TL_('When Microsoft asks which account to use, choose your Work or school account, not your Personal account.'));
      break;
  }

  const continueLabel = (() => {
    if (provider === 'google') {
      return i18n.getString(_TL_('Sign in with Google'));
    }
    if (provider === 'microsoft') {
      return i18n.getString(_TL_('Sign in with Microsoft'));
    }
    return i18n.getString(_TL_('Continue with Microsoft'));
  })();

  return (
    <LoginErrorCard
      idPrefix={`login-${provider}-account-required`}
      title={title}
      actions={(
        <>
          <Button
            className={`login-${provider}-account-continue`}
            appearance="primary"
            icon={<Image src={isGoogle ? GoogleIcon : MicrosoftIcon} width={16} height={16} alt="" aria-hidden="true" />}
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
          <Button className={`login-${provider}-account-go-back`} appearance="secondary" onClick={onGoBack}>
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </>
      )}
    >
      <Text block>{description}</Text>
      <Link
        className={`login-${provider}-account-learn-more`}
        href={LEARN_MORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onLearnMore && onLearnMore()}
      >
        {i18n.getString(_TL_('Learn More'))}
      </Link>
    </LoginErrorCard>
  );
};

export const WorkAccountRequiredPage = (props: Omit<ProviderAccountRequiredPageProps, 'provider'>) => (
  <ProviderAccountRequiredPage {...props} provider="work" />
);

export const PersonalAccountRequiredPage = (props: Omit<ProviderAccountRequiredPageProps, 'provider'>) => (
  <ProviderAccountRequiredPage {...props} provider="personal" />
);

export const GoogleAccountRequiredPage = (props: Omit<ProviderAccountRequiredPageProps, 'provider'>) => (
  <ProviderAccountRequiredPage {...props} provider="google" />
);

// Cross-provider switch: the email is registered under a Microsoft identity, but the user signed in
// with a different provider (e.g. Google). Mirrors the Google card, prompting a Microsoft sign-in.
export const MicrosoftAccountRequiredPage = (props: Omit<ProviderAccountRequiredPageProps, 'provider'>) => (
  <ProviderAccountRequiredPage {...props} provider="microsoft" />
);
